import React, { useEffect, useRef, useState } from 'react';

import { validate } from 'pixkey';

import { FormHandles } from '@unform/core';

import { Box, Divider, Modal, Slide } from '@material-ui/core';

import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { FiEdit3 } from 'react-icons/fi';
import { BiKey, BiX } from 'react-icons/bi';
import { RiBarcodeFill } from 'react-icons/ri';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';

import api from '../../services/api';

import * as S from './styles';

import Toggle from '../../newComponents/Toggle';
import Button from '../../newComponents/Button';
import Input from '../../newComponents/UnformInput';
import Select, { SelectOptionType } from '../../newComponents/Select';
import { formaTemChave, lerChave } from '../../utils/chaveFormaPagamento';

interface Option {
  id: number;
  active: boolean;
  pdvCode: number | string | null;
  image: string;
  acronym: string;
  descriptionPaymentMethod: string;
  descriptionPaymentMethodEntity: string | null;
  // Mapeamento fiscal tPag (Story 1-4 / T1.39)
  paymentFiscalId: number | null;
}

const FormOfPayment: React.FC = () => {
  const formRef = useRef<FormHandles>(null);

  const { user } = useAuth();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [formPayment, setFormPayment] = useState<Option[] | null>(null);
  const [modalEditPaymentIsOpen, setModalEditPaymentIsOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Option | null>(null);
  // Mapeamento fiscal tPag (Story 1-4 / T1.39)
  const [fiscalOptions, setFiscalOptions] = useState<SelectOptionType[]>([]);
  const [emissaoHabilitada, setEmissaoHabilitada] = useState(false);

  const handlePayment = (paymentId: number): void => {
    const foundPayment = formPayment?.find((item) => item.id === paymentId);

    if (foundPayment) {
      setSelectedPayment(foundPayment);
      setModalEditPaymentIsOpen(true);
    }
  };

  const handleCloseModalEditPayment = (): void => {
    setModalEditPaymentIsOpen(false);
    setSelectedPayment(null);
  };

  const changePdvCode = (value: string): void => {
    setSelectedPayment((oldState) =>
      oldState
        ? {
            ...oldState,
            pdvCode: value,
          }
        : oldState,
    );
  };

  const changePaymentFiscal = (value: number | null): void => {
    setSelectedPayment((oldState) =>
      oldState
        ? {
            ...oldState,
            paymentFiscalId: value,
          }
        : oldState,
    );
  };

  const changeKeyPix = (value: string): void => {
    formRef.current?.setErrors({});

    if (value.length > 0 && validate(value).length <= 0) {
      formRef.current?.setErrors({
        keyPix: 'Chave inválida',
      });
    }

    setSelectedPayment((oldState) =>
      oldState
        ? {
            ...oldState,
            descriptionPaymentMethodEntity: JSON.stringify({ chave: value }),
          }
        : oldState,
    );
  };

  const handleChangeAtivo = async (
    paymentId: number,
    value: boolean,
  ): Promise<void> => {
    setIsLoading(true);

    try {
      await api.patch(`/formas_pagamento_empresa/${user.empresa_id}`, {
        forma_pagamento_empresa_id: paymentId,
        ativo: value,
      });

      addToast({
        type: 'success',
        title: `${value ? 'Ativado' : 'Desatvado'} com sucesso`,
      });

      setFormPayment((oldState) => {
        return oldState
          ? oldState.map((item) =>
              item.id === paymentId ? { ...item, active: value } : item,
            )
          : oldState;
      });
      handleCloseModalEditPayment();
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      addToast({
        type: 'error',
        title: 'Erro ao editar forma de pagamento',
      });
      setIsLoading(false);
    }
  };

  const handleSavePayment = async (): Promise<void> => {
    formRef.current?.setErrors({});

    // tPag obrigatório quando a empresa emite NFC-e (Story 1-4 / T1.39 - AC10).
    if (emissaoHabilitada && !selectedPayment?.paymentFiscalId) {
      addToast({
        type: 'error',
        title: 'Mapeamento fiscal obrigatório',
        description:
          'Selecione o mapeamento fiscal (tPag) para esta forma de pagamento.',
      });
      return;
    }

    const chave = lerChave(
      selectedPayment?.descriptionPaymentMethodEntity,
      selectedPayment?.descriptionPaymentMethod,
    );

    if (!chave || validate(chave).length > 0) {
      try {
        setIsLoading(true);

        await api.patch(`/formas_pagamento_empresa/${user.empresa_id}`, {
          forma_pagamento_empresa_id: selectedPayment?.id,
          ativo: selectedPayment?.active,
          codigo_pdv: selectedPayment?.pdvCode,
          // Forma com chave sem chave legível (ex.: o nome da forma gravado
          // pela criação automática): limpa em vez de regravar o valor ruim,
          // que quebra quem lê a chave. O backend só ignora null/undefined.
          descricao:
            formaTemChave(selectedPayment?.descriptionPaymentMethod) && !chave
              ? ''
              : selectedPayment?.descriptionPaymentMethodEntity,
          forma_pagamento_fiscal_id: selectedPayment?.paymentFiscalId ?? null,
        });

        addToast({
          type: 'success',
          title: 'Alterado com sucesso',
        });

        setFormPayment((oldState) => {
          return oldState
            ? oldState.map((item) =>
                selectedPayment && item.id === selectedPayment.id
                  ? selectedPayment
                  : item,
              )
            : oldState;
        });
        handleCloseModalEditPayment();
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        addToast({
          type: 'error',
          title: 'Erro ao editar forma de pagamento',
        });
        setIsLoading(false);
      }
    } else {
      formRef.current?.setErrors({
        keyPix: 'Chave inválida',
      });

      addToast({
        type: 'error',
        title: 'Erro ao editar forma de pagamento',
        description: 'Chave inválida',
      });
    }
  };

  useEffect(() => {
    async function loadFormas(): Promise<void> {
      try {
        const responseFormas = await api.get('/formas_pagamento');
        const { data } = await api.get(
          `/formas_pagamento_empresa/${user.empresa_id}`,
        );

        // Mapeamento fiscal tPag (Story 1-4 / T1.39)
        const [{ data: fiscais }, { data: empresa }] = await Promise.all([
          api.get('/formas-pagamento-fiscais'),
          api.get(`/empresa/${user.empresa_id}`),
        ]);
        setFiscalOptions(
          (fiscais || []).map((f: any) => ({
            value: f.id,
            label: f.codigo ? `${f.codigo} - ${f.descricao}` : f.descricao,
          })),
        );
        setEmissaoHabilitada(!!empresa.emissao_habilitada);

        const options: Option[] = [];

        await Promise.all(
          responseFormas.data.map(
            async (item: any): Promise<void> => {
              const optionType: Option = {
                id: 0,
                descriptionPaymentMethod: item.descricao,
                image: item.imagem,
                acronym: item.sigla,
                active: false,
                pdvCode: null,
                descriptionPaymentMethodEntity: '',
                paymentFiscalId: null,
              };

              data.map((_formPayment: any) => {
                if (item.id === _formPayment.forma_pagamento_id) {
                  optionType.id = _formPayment.id;
                  optionType.active = _formPayment.ativo;
                  optionType.pdvCode = _formPayment.codigo_pdv;
                  optionType.descriptionPaymentMethodEntity =
                    _formPayment.descricao;
                  optionType.paymentFiscalId =
                    _formPayment.forma_pagamento_fiscal_id ?? null;
                }
              });

              if (!optionType.id) {
                const newPaymentMethod = await api.post(
                  '/formas_pagamento_empresa',
                  {
                    data: {
                      codigo_pdv: undefined,
                      ativo: false,
                      forma_pagamento_id: item.id,
                      // Nas formas com chave, `descricao` guarda a chave em
                      // JSON; o nome da forma ali quebrava a leitura.
                      descricao: formaTemChave(item.descricao)
                        ? undefined
                        : item.descricao,
                    },
                    empresa_id: user.empresa_id,
                  },
                );

                optionType.id = newPaymentMethod.data.id;
                optionType.active = newPaymentMethod.data.ativo;
                optionType.pdvCode = newPaymentMethod.data.codigo_pdv;
              }

              options.push(optionType);
            },
          ),
        );

        setFormPayment(options);
        setIsLoading(false);
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro ao buscar dados. Tente Novamente.',
        });
        setIsLoading(false);
      }
    }

    loadFormas();
  }, [addToast, user.empresa_id]);

  return (
    <S.Container>
      <div className="header">
        <h2>Selecione as formas de pagamento que sua loja aceita.</h2>
      </div>

      <Divider />

      {formPayment && (
        <>
          <S.ContainerList>
            <div className="header">
              <h3>Cartão</h3>
            </div>

            <S.CustomTable>
              <S.TableHeader>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Imagem
                  </th>
                </S.TableCell>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Nome
                  </th>
                </S.TableCell>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Código Pdv
                  </th>
                </S.TableCell>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Descrição
                  </th>
                </S.TableCell>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Editar
                  </th>
                </S.TableCell>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Status
                  </th>
                </S.TableCell>
                <S.TableCell />
              </S.TableHeader>
              <S.TableBody>
                <DragDropContext
                  onDragEnd={() => {
                    //
                  }}
                >
                  <Droppable droppableId="droppableFormPaymentGroupOther">
                    {(droppableProvided) => (
                      <tr
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {formPayment
                          .filter(
                            (item) =>
                              item.acronym === 'crédito' ||
                              item.acronym === 'débito',
                          )
                          .map((item) => (
                            <Draggable
                              key={item.id}
                              draggableId={String(item.id)}
                              index={item.id}
                            >
                              {(draggableProvided) => (
                                <td
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                >
                                  <S.TableRow>
                                    <S.TableCellRow>
                                      <img
                                        src={`${process.env.REACT_APP_SERVER_URL}/files/${item.image}`}
                                        alt="type-payment"
                                      />
                                    </S.TableCellRow>
                                    <S.TableCellRow>
                                      <p>{item.descriptionPaymentMethod}</p>
                                    </S.TableCellRow>
                                    <S.TableCellRow>
                                      <p>{item.pdvCode}</p>
                                    </S.TableCellRow>
                                    <S.TableCellRow>
                                      <p>
                                        {item.descriptionPaymentMethodEntity}
                                      </p>
                                    </S.TableCellRow>
                                    <S.TableCellRow>
                                      <FiEdit3
                                        size={20}
                                        onClick={() =>
                                          !isLoading && handlePayment(item.id)
                                        }
                                      />
                                    </S.TableCellRow>
                                    <S.TableCellRow>
                                      <Toggle
                                        checked={item.active}
                                        firstText="Pausado"
                                        secondText="Ativado"
                                        onToggle={(value) =>
                                          !isLoading &&
                                          handleChangeAtivo(item.id, value)
                                        }
                                      />
                                    </S.TableCellRow>
                                  </S.TableRow>
                                </td>
                              )}
                            </Draggable>
                          ))}
                      </tr>
                    )}
                  </Droppable>
                </DragDropContext>
              </S.TableBody>
            </S.CustomTable>
          </S.ContainerList>

          <S.ContainerList>
            <div className="header">
              <h3>Outros</h3>
            </div>

            <S.CustomTable>
              <S.TableHeader>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Imagem
                  </th>
                </S.TableCell>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Nome
                  </th>
                </S.TableCell>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Código Pdv
                  </th>
                </S.TableCell>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Descrição
                  </th>
                </S.TableCell>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Editar
                  </th>
                </S.TableCell>
                <S.TableCell>
                  <th style={{ justifyContent: 'center', display: 'flex' }}>
                    Status
                  </th>
                </S.TableCell>
                <S.TableCell />
              </S.TableHeader>
              <S.TableBody>
                <DragDropContext
                  onDragEnd={() => {
                    //
                  }}
                >
                  <Droppable droppableId="droppableFormPaymentGroupCard">
                    {(droppableProvided) => (
                      <tr
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {formPayment
                          .filter(
                            (item) =>
                              item.acronym !== 'crédito' &&
                              item.acronym !== 'débito',
                          )
                          .map((item) => (
                            <Draggable
                              key={item.id}
                              draggableId={String(item.id)}
                              index={item.id}
                            >
                              {(draggableProvided) => (
                                <td
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                >
                                  <S.TableRow>
                                    <S.TableCellRow>
                                      <img
                                        src={`${process.env.REACT_APP_SERVER_URL}/files/${item.image}`}
                                        alt="type-payment"
                                      />
                                    </S.TableCellRow>
                                    <S.TableCellRow>
                                      <p>{item.descriptionPaymentMethod}</p>
                                    </S.TableCellRow>
                                    <S.TableCellRow>
                                      <p>{item.pdvCode}</p>
                                    </S.TableCellRow>
                                    <S.TableCellRow>
                                      <p>
                                        {formaTemChave(
                                          item.descriptionPaymentMethod,
                                        )
                                          ? lerChave(
                                              item.descriptionPaymentMethodEntity,
                                              item.descriptionPaymentMethod,
                                            )
                                          : item.descriptionPaymentMethodEntity}
                                      </p>
                                    </S.TableCellRow>
                                    <S.TableCellRow>
                                      <FiEdit3
                                        size={20}
                                        onClick={() =>
                                          !isLoading && handlePayment(item.id)
                                        }
                                      />
                                    </S.TableCellRow>
                                    <S.TableCellRow>
                                      <Toggle
                                        checked={item.active}
                                        firstText="Pausado"
                                        secondText="Ativado"
                                        onToggle={(value) =>
                                          !isLoading &&
                                          handleChangeAtivo(item.id, value)
                                        }
                                      />
                                    </S.TableCellRow>
                                  </S.TableRow>
                                </td>
                              )}
                            </Draggable>
                          ))}
                      </tr>
                    )}
                  </Droppable>
                </DragDropContext>
              </S.TableBody>
            </S.CustomTable>
          </S.ContainerList>
        </>
      )}

      <Modal
        open={modalEditPaymentIsOpen}
        onClose={handleCloseModalEditPayment}
        closeAfterTransition
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Slide
          direction="up"
          in={modalEditPaymentIsOpen}
          mountOnEnter
          unmountOnExit
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              width: 'fit-content',
              padding: '20px',
              height: 'fit-content',
              background: '#fff',
              borderRadius: '4px',
              position: 'relative',
              gap: '8px',
              maxWidth: '350px',
            }}
          >
            <S.ModalHeader>
              <h1>Edição</h1>
              <button type="button" onClick={handleCloseModalEditPayment}>
                <BiX />
              </button>
            </S.ModalHeader>
            <S.ModalForm ref={formRef} onSubmit={handleSavePayment}>
              <Input
                name="pdvCode"
                icon={RiBarcodeFill}
                label="Código PDV:"
                placeholder="Ex.: 001"
                disabled={isLoading}
                value={selectedPayment?.pdvCode || ''}
                onChange={(e) => changePdvCode(e.target.value)}
              />
              <Select
                label={`Mapeamento fiscal (tPag)${
                  emissaoHabilitada ? ' *' : ''
                }`}
                placeholder="Selecione o tPag"
                isDisabled={isLoading}
                isClearable
                options={fiscalOptions}
                value={
                  fiscalOptions.find(
                    (o) => o.value === selectedPayment?.paymentFiscalId,
                  ) || null
                }
                onChange={(option: any) =>
                  changePaymentFiscal(option ? option.value : null)
                }
              />
              {formaTemChave(selectedPayment?.descriptionPaymentMethod) && (
                <Input
                  name="keyPix"
                  icon={BiKey}
                  label={`Chave ${selectedPayment?.descriptionPaymentMethod}`}
                  disabled={isLoading}
                  value={lerChave(
                    selectedPayment?.descriptionPaymentMethodEntity,
                    selectedPayment?.descriptionPaymentMethod,
                  )}
                  onChange={(e) => changeKeyPix(e.target.value)}
                />
              )}
              <Button full type="submit" disabled={isLoading}>
                Confirmar
              </Button>
              <Button
                full
                variant="outlined"
                onClick={handleCloseModalEditPayment}
                disabled={isLoading}
                type="button"
              >
                Cancelar
              </Button>
            </S.ModalForm>
          </Box>
        </Slide>
      </Modal>
    </S.Container>
  );
};

export default FormOfPayment;
